/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Container, Nav, NavItem, NavLink } from 'reactstrap';
import { withRouter, Route } from 'react-router-dom';
import { DisplayForTeamAdmin } from '@shoflo/polymer/helpers/DisplayForRole/DisplayForRole';
import DashRedirect from 'containers/DashRedirect';
import TeamUserDashDropdown from 'containers/TeamUserDashDropdown';
import PageHeader from 'containers/PageHeader';
import { getTeam } from 'actions/Team';
import UpcomingEvents from 'containers/TeamDashUpcomingEvents';
import ArchivedEvents from 'containers/TeamDashArchivedEvents';
import PastEvents from 'containers/TeamDashPastEvents';
import Admins from 'containers/TeamDashAdmins';
import Tooltip from 'components/Tooltip';

const StyledNavLink = styled(NavLink)`
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  font-size: 16px;
  cursor: pointer;
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  color: ${(props) =>
    props.active ? '#0095FF !important;' : '#767676 !important;'};
  border-bottom: ${(props) =>
    props.active ? '2px solid #0095FF !important;' : ''};
  margin: 0 12px !important;
  padding: 8px 0;

  &:hover {
    border: none;
    color: ${(props) =>
      props.active ? '#0095FF !important;' : '#333 !important;'};
  }

  @media only screen and (max-width: 360px) {
    font-size: 12px;
    margin: 0 8px;
  }
`;

const NavWrapper = styled.div`
  position: sticky;
  top: 186px;
  background: #fff;
  width: 100vw;
  margin: 0 -48px;
  margin-bottom: -4px;
  border-bottom: 2px solid #c8c8c8;
  z-index: 3;

  @media only screen and (max-width: 576px) {
    top: 172px;
  }
`;

const StyledNavTabs = styled(Nav)`
  border-bottom: none !important;
  margin: 0 32px;
  @media only screen and (max-width: 576px) {
    margin: 0 0 0 32px;
  }

  @media only screen and (max-width: 375px) {
    margin: 0 0 0 24px;
  }

  .nav-item {
    margin-bottom: -2px;
  }
`;
// eslint-disable-next-line react/display-name
const Unconnected = React.memo((props) => {
  const {
    isTeamAdmin,
    getTeam,
    match,
    team,
    user,
  } = props;
  const { params } = match;
  const { teamId } = params;

  useEffect(() => {
    getTeam();
  }, [getTeam, teamId, user]);

  if (!team) return <div />;

  if (!isTeamAdmin) return <DashRedirect />;

  if (document.title !== team.title) document.title = team.title;

  const { title: teamTitle } = team;

  const teamPhoto = team && team.photo ? team.photo : '';

  return (
    <DisplayForTeamAdmin teamId={teamId} user={user}>
      <Container fluid>
        <PageHeader
          title={<TeamUserDashDropdown />}
          subtitle="Team dashboard"
          rightImage={teamPhoto}
          rightImageTitle={`${teamTitle} team logo`}
        />
        <Route exact path={`${match.url}`} component={ConnectedTabs} />
        <Route path={`${match.url}/:subDir`} component={ConnectedTabs} />
        <Route exact path={`${match.url}`} component={UpcomingEvents} />
        <Route
          exact
          path={`${match.url}/archived`}
          component={ArchivedEvents}
        />
        <Route exact path={`${match.url}/past-events`} component={PastEvents} />
        <Route exact path={`${match.url}/admins`} component={Admins} />
      </Container>
    </DisplayForTeamAdmin>
  );
});

function Tabs({ history, match, team }) {
  const navRef = useRef();
  const [navTop, setNavTop] = useState(0);

  useEffect(() => {
    if (team) {
      setNavTop(navRef.current.getBoundingClientRect().top);
    }
  }, [navRef, setNavTop, team]);

  if (!team) return null;

  const { _id: teamId } = team;

  const { subDir } = match.params;
  const {
    location: { pathname },
  } = history;

  return (
    <NavWrapper ref={navRef} stickyTop={`${navTop}px`}>
      <StyledNavTabs tabs>
        <NavItem>
          <Tooltip
            id="team-dash-tabs-upcoming"
            title="View all active Events"
            visible={!!subDir}
          />
          <StyledNavLink
            active={!subDir}
            aria-describedby="tip-team-dash-tabs-upcoming"
            data-testid="upcomingEventTab"
            onClick={() =>
              pathname !== `/dash/team/${teamId}` &&
              history.push(`/dash/team/${teamId}`)
            }
          >
            Upcoming events
          </StyledNavLink>
        </NavItem>
        <NavItem>
          <Tooltip
            id="team-dash-tabs-past-events"
            title="View all past events"
            visible={!subDir}
          />
          <StyledNavLink
            aria-describedby="tip-team-dash-tabs-past-events"
            data-testid="pastEventTab"
            active={subDir === 'past-events'}
            onClick={() =>
              pathname !== `/dash/team/${teamId}/past-events` &&
              history.push(`/dash/team/${teamId}/past-events`)
            }
          >
            Past events
          </StyledNavLink>
        </NavItem>
        <NavItem>
          <Tooltip
            id="team-dash-tabs-archived"
            title="View all manually archived Events"
            visible={!subDir}
          />
          <StyledNavLink
            aria-describedby="tip-team-dash-tabs-archived"
            data-testid="archivedTab"
            active={subDir === 'archived'}
            onClick={() =>
              pathname !== `/dash/team/${teamId}/archived` &&
              history.push(`/dash/team/${teamId}/archived`)
            }
          >
            Archived events
          </StyledNavLink>
        </NavItem>
        <NavItem className="d-none d-sm-block">
          <StyledNavLink
            data-testid="teamAdminsTab"
            active={subDir === 'admins'}
            onClick={() =>
              pathname !== `/dash/team/${teamId}/admins` &&
              history.push(`/dash/team/${teamId}/admins`)
            }
          >
            Team admins
          </StyledNavLink>
        </NavItem>
      </StyledNavTabs>
    </NavWrapper>
  );
}

const ConnectedTabs = withRouter(
  connect(({ team }) => {
    return { team };
  }, {})(Tabs),
);

export default withRouter(
  connect(
    (state) => {
      const {
        globalElementManager,
        isTeamAdmin,
        team,
        teamEvents,
        user,
      } = state;

      return {
        isTeamAdmin,
        globalElementManager,
        team,
        teamEvents,
        user,
      };
    },
    {
      getTeam,
    },
  )(Unconnected),
);

export { Tabs };
